import { forwardRef } from 'react';
import {
  faCalendar,
  faChevronLeft,
  faChevronRight,
  faCircleExclamation,
  faDice,
  faFileExcel,
  faFilter,
} from '@fortawesome/free-solid-svg-icons';
import dayjs from 'dayjs';
import ReactDatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Clock } from '@/core/date/export';
import { Menu } from '@/core/menu/export';
import { useOpenWidget } from '@/core/widget-navigation/export';
import { FORECAST_FRENZY_WIDGET_ID } from '@/modules/calendar/services/forecast-game-service';
import { forecastFrenzyWidget } from '@/modules/calendar/widgets/forecast-frenzy-widget';
import { getDeepDiveApp } from '@/core/deep-dive/export';
import { useWidgetPermission } from '@/core/widgets/export';

const dateFormat = 'YYYY-MM-DD';

export default function CalendarMenu({
  date,
  view,
  onNavigate,
  onToggleFilters,
  filtersActive,
  notifications,
  notificationError,
  notificationLoaded,
  reloadNotifications,
}: {
  date: string;
  view: 'daily' | 'weekly';
  onNavigate: ({ date, view }: { date: string; view: 'daily' | 'weekly' }) => void;
  onToggleFilters: () => void;
  filtersActive?: boolean;
  notifications: string[];
  notificationError: any;
  notificationLoaded: boolean;
  reloadNotifications: () => void;
}) {
  const openWidget = useOpenWidget();
  const registeredForecastFrenzyWidget =
    getDeepDiveApp(FORECAST_FRENZY_WIDGET_ID) ?? forecastFrenzyWidget;
  const { authorized: forecastFrenzyAuthorized } = useWidgetPermission(
    registeredForecastFrenzyWidget,
  );
  const unit = (view: string): 'week' | 'day' => (view === 'weekly' ? 'week' : 'day');
  const next = (date: string) => {
    onNavigate({
      date: dayjs(date).tz().add(1, unit(view)).format(dateFormat),
      view: view,
    });
    //setDate(dayjs(date).tz().add(1, unit(view)).format(dateFormat));
  };

  const prev = (date: string) => {
    onNavigate({
      date: dayjs(date).tz().subtract(1, unit(view)).format(dateFormat),
      view: view,
    });
    //setDate(dayjs(date).tz().subtract(1, unit(view)).format(dateFormat));
  };

  const today = () => {
    onNavigate({
      date: 'today',
      view: view,
    });
    //setDate('today');
  };

  const daily = () => {
    onNavigate({
      date: date,
      view: 'daily',
    });
    //setView('daily');
  };

  const weekly = () => {
    onNavigate({
      date: date,
      view: 'weekly',
    });
    //setView('weekly');
  };

  return (
    <>
      {notificationLoaded && notificationError && (
        <div className={'alert alert-danger'}>
          Error connecting to remote service.
          <button
            className={'btn btn-link'}
            onClick={reloadNotifications}
            style={{ fontFamily: 'Inter' }}
          >
            Retry
          </button>
        </div>
      )}
      {notificationLoaded && !notificationError && notifications.length > 0 && (
        <div className="mb-3">
          {notifications.map(notification => (
            <div
              key={notification}
              className={'alert alert-warning alert-sm small text-center p-1 mb-1'}
            >
              {notification}
            </div>
          ))}
        </div>
      )}
      <div className={'d-flex pt-1 align-items-center'}>
        <span className={'me-2 d-container-sm-block d-container-none'}>
          <img alt="logo" src={'/logo32.png'} height={32} width={32} />
        </span>
        <span className={'w-25'}>
          <select className={'form-select form-select-sm text-neutral-500 btn-menu'}>
            <option value={''}>All Data</option>
          </select>
        </span>
        <span>
          <button
            className={`btn btn-sm text-primary-500 ms-1`}
            style={{ backgroundImage: 'none' }}
            onClick={() => onToggleFilters()}
            key={'filter-' + date}
          >
            <FontAwesomeIcon icon={faFilter} />
            {filtersActive && (
              <FontAwesomeIcon className={'text-warning-light ms-1'} icon={faCircleExclamation} />
            )}
          </button>
        </span>

        <span className={'ms-auto me-auto small'}>
          <Clock />
        </span>
        <span>
          <div className="d-flex align-items-center" role="group" aria-label="Basic example">
            {(() => {
              const DatePickerButton = forwardRef<any, any>(({ onClick }, ref) => (
                <button
                  className={`btn btn-sm btn-outline-secondary text-secondary btn-menu date-picker`}
                  onClick={onClick}
                  ref={ref}
                >
                  <span className={'d-container-none d-container-md-inline'}>
                    {dayjs(date).tz().format('MM-DD')}
                  </span>
                  <span className={'d-container-inline d-container-md-none'}>
                    <FontAwesomeIcon icon={faCalendar} />
                  </span>
                </button>
              ));

              return (
                <ReactDatePicker
                  selected={dayjs(date).tz().toDate()}
                  onChange={date => {
                    onNavigate({
                      date: dayjs(date).tz().format(dateFormat),
                      view: view,
                    });
                  }}
                  todayButton={
                    <button
                      style={{ fontFamily: 'Inter' }}
                      className={'btn btn-sm btn-secondary text-secondary text-base-light'}
                    >
                      Today
                    </button>
                  }
                  customInput={<DatePickerButton />}
                />
              );
            })()}
            <div className={'btn-group btn-group-sm d-block ms-1'}>
              <button
                className={`btn btn-sm btn-outline-secondary text-secondary hide-xxs btn-menu`}
                onClick={() => prev(date)}
                key={'prev-' + date}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button
                style={{ fontFamily: 'Inter' }}
                className={`btn btn-sm btn-outline-secondary text-secondary hide-xxs btn-menu`}
                onClick={() => today()}
              >
                <span className={'d-container-none d-container-md-inline'}>Today</span>
                <span className={'d-container-inline d-container-md-none'}>T</span>
              </button>
              <button
                className={`btn btn-sm btn-outline-secondary text-secondary hide-xxs btn-menu`}
                onClick={() => next(date)}
                key={'next-' + date}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </span>
        <div className="btn-group ms-1" role="group" aria-label="Basic example">
          <span className={'d-container-none d-container-sm-inline'}>
            <button
              style={{ fontFamily: 'Inter' }}
              className={`btn btn-sm ${
                view === 'daily'
                  ? 'btn-secondary text-base-light'
                  : 'btn-outline-secondary text-secondary btn-menu'
              }`}
              onClick={() => daily()}
            >
              <span className={'d-container-none d-container-md-inline'}>Daily</span>
              <span className={'d-container-md-none'}>D</span>
            </button>
            <button
              style={{ fontFamily: 'Inter' }}
              className={`btn btn-sm ${
                view === 'weekly'
                  ? 'btn-secondary text-base-light'
                  : 'btn-outline-secondary text-secondary btn-menu'
              }`}
              onClick={() => weekly()}
            >
              <span className={'d-container-none d-container-md-inline'}>Weekly</span>
              <span className={'d-container-md-none'}>W</span>
            </button>
          </span>
          <span className={'d-container-inline d-container-sm-none hide-xxs'}>
            <button
              style={{ fontFamily: 'Inter' }}
              className={`btn btn-sm btn-outline-secondary text-secondary btn-menu `}
              onClick={() => (view === 'daily' ? weekly() : daily())}
            >
              <sup className={view === 'daily' ? 'text-secondary-light' : ''}>D</sup>/
              <sub className={view === 'weekly' ? 'text-secondary-light' : ''}>W</sub>
            </button>
          </span>
        </div>
        <span>
          <button
            className={'btn btn-sm btn-warning ms-1'}
            style={{ fontFamily: 'Inter' }}
            title={'MNI DataLink for Excel'}
            onClick={() =>
              window.open(
                'https://marketplace.microsoft.com/en-us/product/WA200010666',
                'mni_datalink',
              )
            }
          >
            <FontAwesomeIcon icon={faFileExcel} />
            <span className={'d-container-none d-container-md-inline ms-1'}>DataLink</span>
          </button>
        </span>
        {forecastFrenzyAuthorized && (
          <span>
            <button
              className={'btn btn-sm btn-outline-secondary text-secondary btn-menu ms-1'}
              title={'Forecast Frenzy'}
              onClick={() => openWidget(FORECAST_FRENZY_WIDGET_ID)}
            >
              <FontAwesomeIcon icon={faDice} />
            </button>
          </span>
        )}
        <span>
          <Menu />
        </span>
      </div>
    </>
  );
}
